const PLANS = [
  {
    icon: 'ri-parent-line',
    accent: 'bg-orange-100',
    accentText: 'text-orange-600',
    label: '子育て世帯向け',
    title: 'キッズ＆ファミリープラン',
    desc: '子どもが自分で片付けられる仕組みづくり。ママ・パパも楽になる、家族みんなが使いやすい整理収納を実現します。',
    items: ['おもちゃ・学用品の収納システム', '子ども部屋の整理サポート', '家事効率化の動線づくり', '家族全員使える収納設計'],
  },
  {
    icon: 'ri-heart-pulse-line',
    accent: 'bg-amber-100',
    accentText: 'text-amber-600',
    label: '介護・シニア向け',
    title: '安心・安全プラン',
    desc: '看護師の視点から、転倒リスクを減らし安全な動線を確保。介護される方もする方も、快適に過ごせる環境を整えます。',
    items: ['バリアフリー動線の整備', '医療・介護用品の整理収納', '緊急時に備えた物の配置', '安全で清潔な住環境づくり'],
  },
];

const FLOW = [
  { step: '01', title: 'お問い合わせ', desc: 'まずはお気軽にご相談ください。LINE・電話・メールで受付中。' },
  { step: '02', title: '日程調整・ヒアリング', desc: 'お悩みや理想の暮らしについてじっくりお聞きします。' },
  { step: '03', title: 'サービス実施', desc: '訪問またはオンラインで整理収納をサポートします。' },
  { step: '04', title: 'アフターフォロー', desc: '実施後も継続的なサポートで理想の暮らしを維持します。' },
];

const scrollToSection = (id: string) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: 'smooth' });
};

export default function OrganizingSection() {
  return (
    <section id="organizing" className="py-24 bg-[#fff8f3]">
      <div className="max-w-6xl mx-auto px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-center mb-20">
          <div className="relative rounded-2xl overflow-hidden order-2 lg:order-1" style={{ height: '420px' }}>
            <img
              src="https://public.readdy.ai/ai/img_res/a79d2a8a55baeeedcc1fe0af4373556c.jpg"
              alt="整理収納アドバイザー"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute top-6 left-6">
              <div className="bg-orange-500 text-white text-xs font-medium px-4 py-2 rounded-full">
                看護師×整理収納アドバイザー
              </div>
            </div>
          </div>
          <div className="order-1 lg:order-2">
            <p className="text-orange-500 text-sm font-medium tracking-widest uppercase mb-3">整理収納アドバイザー事業</p>
            <h2 className="text-3xl font-bold text-stone-800 mb-6 leading-tight">
              安全・清潔・快適な暮らしを、<br />
              看護師の視点でサポート
            </h2>
            <p className="text-stone-500 leading-relaxed mb-5">
              整理収納は単なる「片付け」ではありません。家族の安全を守り、日々のストレスを減らし、人生の質を高める大切な取り組みです。
            </p>
            <p className="text-stone-500 leading-relaxed mb-8">
              看護師として培った観察力と生活改善の知識を活かし、子育て世帯・介護世帯それぞれのニーズに合わせた<strong className="text-stone-700">オーダーメイドの整理収納プラン</strong>を提供します。
            </p>
            <div className="flex flex-wrap gap-3">
              <a
                href="https://nurse-kataduke.com/"
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="flex items-center gap-2 border border-orange-500 text-orange-500 hover:bg-orange-50 font-medium px-7 py-3 rounded-full transition-all cursor-pointer whitespace-nowrap"
              >
                公式サイトを見る
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-external-link-line text-sm"></i>
                </div>
              </a>
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6 mb-16">
          {PLANS.map((plan) => (
            <div key={plan.label} className="bg-white rounded-2xl p-8 border border-stone-100">
              <div className="flex items-center gap-3 mb-5">
                <div className={`w-11 h-11 ${plan.accent} rounded-full flex items-center justify-center`}>
                  <i className={`${plan.icon} ${plan.accentText} text-xl`}></i>
                </div>
                <div>
                  <span className={`text-xs font-medium ${plan.accentText}`}>{plan.label}</span>
                  <h3 className="font-bold text-stone-800 text-base">{plan.title}</h3>
                </div>
              </div>
              <p className="text-stone-500 text-sm leading-relaxed mb-5">{plan.desc}</p>
              <ul className="space-y-2">
                {plan.items.map((item) => (
                  <li key={item} className="flex items-center gap-2 text-stone-600 text-sm">
                    <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                      <i className="ri-check-line text-orange-500"></i>
                    </div>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div>
          <h3 className="text-xl font-bold text-stone-800 mb-8 text-center">ご利用の流れ</h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {FLOW.map((f, i) => (
              <div key={f.step} className="relative bg-white rounded-xl p-6 border border-stone-100">
                {i < FLOW.length - 1 && (
                  <div className="hidden lg:flex absolute -right-2 top-1/2 -translate-y-1/2 z-10 w-4 h-4 items-center justify-center text-stone-300">
                    <i className="ri-arrow-right-s-line text-xl"></i>
                  </div>
                )}
                <div className="text-3xl font-bold text-stone-100 mb-3" style={{ fontFamily: 'Noto Serif JP' }}>{f.step}</div>
                <h4 className="font-semibold text-stone-800 text-sm mb-2">{f.title}</h4>
                <p className="text-stone-400 text-xs leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
